/**
 * The Unassigned panel under a pitch (SE-8391, PV23 · D4): after a formation
 * change, the members whose slot disappeared are listed here with their
 * former slot and rank, placeable in place. Shared by the watchlist pitch and
 * the scenario pitch; hidden when nobody is Unassigned. SE-8598: it opens
 * collapsed to its header so a long list no longer pushes the pitch below the
 * fold; the header toggles the rows.
 * @module features/scouting/components/molecules/PitchUnassignedPanel
 */
import React, { memo, useCallback, useMemo, useState } from "react";

import type { PitchMemberRemoveState } from "@/features/scouting/components/molecules/PitchPositionPlayerLine";
import type { PitchUnassignedSurface } from "@/features/scouting/components/molecules/PitchUnassignedRow";
import type { PitchUnassignedEntry } from "@/features/scouting/utils/pitchUnassigned";
import { formatPitchFormation } from "@/features/scouting/utils/watchlistPitchPresentation";
import type { PitchFormation, TacticalSlot } from "@/generated/graphql";

import PitchUnassignedPanelView from "./PitchUnassignedPanelView";

/** Props for the Unassigned panel. */
export interface PitchUnassignedPanelProps {
  readonly surface: PitchUnassignedSurface;
  /** The current formation, named in the caption. */
  readonly formation: PitchFormation;
  readonly entries: readonly PitchUnassignedEntry[];
  /** Whether rows lift into the pitch DnD boundary. */
  readonly canDrag: boolean;
  /** Place ▾ write; absent = disabled on every row (a viewer). */
  readonly onPlace?: (playerId: string, slot: TacticalSlot | null) => void;
  /** Removes a player from the list / scenario; absent = no Remove. */
  readonly onRemove?: (playerId: string, displayName: string) => void;
  /** Where each removal stands; idle when absent. */
  readonly removeStateById?: Readonly<Record<string, PitchMemberRemoveState>>;
  /** Opens unfolded; the screens leave it closed (SE-8598), the gallery shows both. */
  readonly defaultExpanded?: boolean;
}

/**
 * The caption's second clause, verbatim from Figma 944:16235. The scenario
 * has none: SE-8494 retired its "not available" clause with the control it
 * described, and the frame writes nothing in its place.
 */
const CAPTION_HINT: Readonly<Partial<Record<PitchUnassignedSurface, string>>> =
  { watchlist: "drag onto a position to place them" };

/**
 * Prepares the header and caption copy, and owns the collapsed state
 * (default closed, SE-8598), for the pure panel.
 * @param props - Surface, formation, rows and handlers
 */
const PitchUnassignedPanelContainer = (props: PitchUnassignedPanelProps) => {
  const { entries, formation, surface, canDrag, defaultExpanded } = props;
  // A reader who cannot drag gets the fact without the instruction.
  const caption = useMemo(() => {
    const fact = `No slot in ${formatPitchFormation(formation)}`;
    const hint = canDrag ? CAPTION_HINT[surface] : undefined;
    return hint ? `${fact} · ${hint}` : fact;
  }, [canDrag, formation, surface]);
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const onToggle = useCallback(() => setExpanded(value => !value), []);
  if (entries.length === 0) return null;
  const count = entries.length;
  const noun = count === 1 ? "player" : "players";
  return (
    <PitchUnassignedPanelView
      {...props}
      header={`UNASSIGNED · ${count}`}
      caption={caption}
      expanded={expanded}
      onToggle={onToggle}
      toggleLabel={`${expanded ? "Hide" : "Show"} ${count} unassigned ${noun}`}
    />
  );
};

PitchUnassignedPanelContainer.displayName = "PitchUnassignedPanelContainer";
export default memo(PitchUnassignedPanelContainer);
